import { type FC, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '@components/Modal';
import { useEntityStore } from '@ContractBuilder/store';
import { batchCloneEndorsement } from '@mutations/batchCloneEndorsement';
import { useSubmissions } from '@queries/fetchSubmissions';
import { filter } from 'lodash-es';

import { ToastContent } from '../components';
import { useCloneEndorsementSelection } from '../hooks';
import type { CloneEndorsementModalProps } from '../types';
import { CloneEndorsementContent } from '../views';

export const CloneEndorsementModalController: FC<CloneEndorsementModalProps> = ({ handleClose }) => {
  const [isCloning, setIsCloning] = useState(false);
  const { endorsementId, parentId } = useEntityStore(({ submission, endorsementParent }) => ({
    endorsementId: submission?.id,
    parentId: endorsementParent?.id,
  }));

  const { data, isLoading } = useSubmissions();

  const finalisedContracts = filter(data, ({ status, id }) => status === 'FINAL' && id !== parentId);

  const handleApply = async (contractIds: string[]) => {
    if (!parentId || !endorsementId) {
      toast.error(<ToastContent kind="error" />);
      return;
    }

    try {
      setIsCloning(true);
      await batchCloneEndorsement({
        submissionId: parentId,
        endorsementId,
        contractIds,
      });

      toast.success(<ToastContent kind="success" />);
      return handleClose();
    } catch (error) {
      console.error('Error cloning endorsement', error);
      toast.error(<ToastContent kind="error" />);
    } finally {
      setIsCloning(false);
    }
  };

  const {
    addContract,
    filteredContracts,
    removeContract,
    searchValue,
    selectedContracts,
    selectedCount,
    setSearchValue,
  } = useCloneEndorsementSelection({
    contracts: finalisedContracts,
  });

  return (
    <Modal
      className="gap-0 sm:max-w-[1024px]!"
      onClose={handleClose}
      open
      shouldShowCloseIcon
      title="Duplicate Endorsement to Multiple Contracts"
      titleClassName="justify-start!"
    >
      <CloneEndorsementContent
        filteredContracts={filteredContracts}
        isCloning={isCloning}
        isLoading={isLoading}
        onCancel={handleClose}
        onApply={handleApply}
        addContract={addContract}
        removeContract={removeContract}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        selectedContracts={selectedContracts}
        selectedCount={selectedCount}
      />
    </Modal>
  );
};
